import { useMutation, useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  type InvoiceResponse,
  type ReconcileRequest,
  type SubjectChoice,
  type SubjectPaymentRequest,
  toCents,
  type UseSubjectPaymentRequest,
} from '@k-management/shared';
import { useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { moneyOf } from '../people/people-ui.js';
import {
  EMPTY_NO_INVOICE,
  noInvoiceBody,
  NoInvoiceFields,
  type NoInvoiceValues,
} from './NoInvoicePayment.js';
import { announceTreasuryChange } from './treasury-ui.js';

/**
 * A subject to choose by name or VAT (2.22): what the rules recognised in
 * the movement is proposed; the search reads Anagrafica with the rights of
 * Tesoreria.
 */
export function SubjectPicker({
  value,
  onChange,
  autoFocus = false,
}: {
  value: { id: string; name: string } | null;
  onChange: (subject: { id: string; name: string; noInvoice?: boolean } | null) => void;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  const found = useQuery({
    queryKey: ['treasury', 'subject-choices', query.trim()],
    queryFn: () =>
      apiRequest<SubjectChoice[]>(
        `/treasury/subject-payments/subjects?q=${encodeURIComponent(query.trim())}`,
      ),
    enabled: query.trim().length >= 2,
  });
  return (
    <div className="stack tight">
      {value ? (
        <div className="row between">
          <strong>{value.name}</strong>
          <button type="button" className="link small" onClick={() => onChange(null)}>
            Cambia
          </button>
        </div>
      ) : (
        <>
          <input
            autoFocus={autoFocus}
            value={query}
            placeholder="Cerca per nome o partita IVA"
            onChange={(event) => setQuery(event.target.value)}
          />
          {(found.data ?? []).map((subject) => (
            <button
              key={subject.id}
              type="button"
              className="link small"
              style={{ textAlign: 'left' }}
              onClick={() =>
                onChange({ id: subject.id, name: subject.name, noInvoice: subject.noInvoice })
              }
            >
              {subject.name}
              {subject.vatNumber && <span className="muted"> · {subject.vatNumber}</span>}
              {subject.noInvoice && <span className="pill muted">non emette fattura</span>}
            </button>
          ))}
          {query.trim().length >= 2 && found.data?.length === 0 && (
            <span className="muted small">Nessun soggetto con questo nome.</span>
          )}
        </>
      )}
    </div>
  );
}

/**
 * «Al soggetto, senza documento…» (2.22): the movement, or what is left of
 * it, to a subject whose invoice has not arrived yet. On a movement not
 * reconciled it reconciles it; on one with invoices it takes the part left.
 */
export function SubjectPaymentDialog({
  base,
  reconciled,
  freeCents,
  incoming,
  proposed,
  onClose,
  onDone,
}: {
  /** `/treasury/banks/{code}/movements/{id}`. */
  base: string;
  reconciled: boolean;
  freeCents: number;
  incoming: boolean;
  proposed: { id: string; name: string } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [subject, setSubject] = useState<{
    id: string;
    name: string;
    noInvoice?: boolean;
  } | null>(proposed);
  const [amount, setAmount] = useState(fromCents(freeCents));
  const [note, setNote] = useState('');
  const [details, setDetails] = useState<NoInvoiceValues>(EMPTY_NO_INVOICE);
  const value = moneyOf(amount);
  // The subject proposed from the movement: whether it invoices is read from the choices (3.14).
  const proposedChoice = useQuery({
    queryKey: ['treasury', 'subject-choices', proposed?.name ?? ''],
    queryFn: () =>
      apiRequest<SubjectChoice[]>(
        `/treasury/subject-payments/subjects?q=${encodeURIComponent(proposed!.name.slice(0, 100))}`,
      ),
    enabled: Boolean(proposed && proposed.name.trim().length >= 2),
  });
  const noInvoice =
    subject?.noInvoice ??
    (subject?.id === proposed?.id &&
      Boolean(proposedChoice.data?.find((item) => item.id === subject?.id)?.noInvoice));
  const extra = noInvoice ? noInvoiceBody(details) : {};
  const save = useMutation({
    mutationFn: () =>
      reconciled
        ? apiRequest(`${base}/subject-payments`, {
            method: 'POST',
            body: {
              subjectId: subject!.id,
              amount: value!,
              note: note.trim() || null,
              ...extra,
            } satisfies SubjectPaymentRequest,
          })
        : apiRequest(`${base}/reconciliation`, {
            method: 'PUT',
            body: {
              kind: 'soggetto',
              subjectId: subject!.id,
              amount: value!,
              note: note.trim() || null,
              ...extra,
            } satisfies ReconcileRequest,
          }),
    onSuccess: onDone,
  });
  return (
    <ConfirmDialog
      title={
        noInvoice
          ? 'Pagamento senza fattura'
          : incoming
            ? 'Incasso dal soggetto, senza documento'
            : 'Pagamento al soggetto, senza documento'
      }
      confirmLabel={noInvoice ? 'Registra il costo' : 'Imputa al soggetto'}
      busy={save.isPending}
      disabled={!subject || value === null || toCents(value ?? '0') > freeCents}
      error={save.error?.message}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    >
      <Field label="Soggetto">
        <SubjectPicker value={subject} onChange={setSubject} autoFocus />
      </Field>
      <Field label="Importo">
        <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
      </Field>
      <Field label="Note">
        <input
          value={note}
          maxLength={300}
          placeholder="Es. acconto, fattura da ricevere"
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>
      {noInvoice && <NoInvoiceFields values={details} onChange={setDetails} />}
      <p className="muted small">
        Disponibili {formatMoney(fromCents(freeCents))}.{' '}
        {noInvoice
          ? 'Il soggetto non emette fattura: il pagamento è il costo, con la voce del fornitore in Anagrafica. La quietanza si allega dopo, dalla riga del movimento.'
          : 'L’importo resta nella scheda contabile del soggetto, sezione finanziaria, finché non arriva la fattura: dalla fattura si usa con «Usa», come pagamento di questo movimento.'}
      </p>
    </ConfirmDialog>
  );
}

/**
 * On an invoice still to pay or collect (2.22): the payments without document
 * of its subject, to use on it. The part used becomes a settlement of the
 * same movement, on its day.
 */
export function InvoiceSubjectPayments({
  invoice,
  onDone,
}: {
  invoice: InvoiceResponse;
  onDone: () => Promise<void> | void;
}) {
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const use = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: string }) =>
      apiRequest(`/treasury/subject-payments/${id}/use`, {
        method: 'POST',
        body: { invoiceId: invoice.id, amount } satisfies UseSubjectPaymentRequest,
      }),
    onSuccess: async () => {
      announceTreasuryChange();
      await onDone();
    },
  });
  if (!invoice.subjectPayments.length) return null;
  const issued = invoice.direction === 'emessa';
  return (
    <div className="notice info stack">
      <span>
        {issued ? 'Incassati da' : 'Pagati a'} <strong>{invoice.counterpartyName}</strong> senza
        documento: si possono usare su questa fattura.
      </span>
      <table className="data-table">
        <tbody>
          {invoice.subjectPayments.map((payment) => {
            const proposed = fromCents(
              Math.min(toCents(payment.available), toCents(invoice.residual)),
            );
            const value = moneyOf(amounts[payment.id] ?? proposed);
            return (
              <tr key={payment.id}>
                <td>
                  {formatDate(payment.date)} ·{' '}
                  {payment.card ? `carta •••• ${payment.card.last4}` : payment.accountCode}
                  {payment.note && <span className="muted"> · «{payment.note}»</span>}
                </td>
                <td className="numeric">disponibili {formatMoney(payment.available)}</td>
                <td style={{ width: 140 }}>
                  <DecimalInput
                    value={amounts[payment.id] ?? proposed}
                    onChange={(next) => setAmounts({ ...amounts, [payment.id]: next })}
                    decimals={[2, 2]}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="primary small"
                    disabled={use.isPending || value === null}
                    onClick={() => use.mutate({ id: payment.id, amount: value! })}
                  >
                    Usa
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {use.error && <p className="error">{use.error.message}</p>}
    </div>
  );
}
